import ProjectsHome from './components/ProjectsHome';
import AccountPage from './components/AccountPage';
import ProjectTitle from './components/ProjectTitle';
import {acousticBoundariesEnabled} from './lib/boundaryFeature';
import {isEditorTabActive} from './lib/editorTabs';
import {projectInterviews, interviewStatus, appendEarlierStory} from './lib/projectWorkspace';
import {originalInterviewFilename,interviewDisplayName,interviewAlias,updateStorySourceLabel} from './lib/interviewNames';
import {recallPaperInterview,storePaperInterview,resolvePaperInterview} from './lib/paperInterviewSelection';
import {interviewColour,sourceColourOrder,storyWordIds} from './lib/storyContext';
import {readVideoMetadata,hasInterviewAudioTrack} from './lib/importMedia';
import {inspectMp4TimingFile} from './lib/mediaInfo';
import {createReviewProxy} from './lib/proxyMedia';
import {estimateProxyBytes,shouldOfferProxy,PROXY_MAX_SOURCE_BYTES} from './lib/proxySafety';
import {selectedRanges} from './lib/selection';
import React,{useEffect,useRef,useState} from 'react';
import {createPortal} from 'react-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import PaperEdit from './components/PaperEdit';
import {syncActiveAssemblyCut,activeCutName,assemblyCutExportSuffix,readAssemblyCuts} from './lib/assemblyCuts.js';
import RefineReview from './components/RefineReview';
import FindSelectsButton from './components/FindSelectsButton';
import MeaningSearch from './components/MeaningSearch';
import AiCleanup from './components/AiCleanup';
import {sourceSnapshot,addPassages} from './lib/paperEdit';
import {markSourceWords,sourceWordPriority} from './lib/storyPriority.js';
import {cleanupProtectedRanges,safeCleanupSuggestions,applyCleanupToInterview,cleanupSignature} from './lib/aiCleanup.js';
import {primePaperPaste,playPaperPaste} from './lib/paperPasteSound';
import ReviewManager from './components/ReviewManager';
import ExportDialog from './components/ExportDialog';
import Transcript from './components/Transcript';
import Preview from './components/Preview';
import Timeline from './components/Timeline';
import AuthDialog from './components/AuthDialog';
import GretaHome from './marketing/GretaHome';
import useSupabaseAuth from './hooks/useSupabaseAuth';
import useCloudProjects from './hooks/useCloudProjects';
import MediaThumbnail from './components/MediaThumbnail';
import BatchImportProgress from './components/BatchImportProgress';
import MediaLibraryPicker from './components/MediaLibraryPicker';
import MediaAddMenu from './components/MediaAddMenu';
import ProjectDeleteDialog from './components/ProjectDeleteDialog';
import ProjectMergeDialog from './components/ProjectMergeDialog';
import FileActionsMenu from './components/FileActionsMenu';
import CloudImportDialog from './components/CloudImportDialog';
import TranslationDialog from './components/TranslationDialog';
import {demoProject} from './lib/demo';
import {clamp,isRemoved} from './lib/editModel';
import {sequenceClips,sequenceDuration,removeSourceRange,restoreSourceRange} from './lib/sequence';
import {buildSrt,buildText,buildXml,downloadFile,downloadBlob} from './lib/exports';
import {buildFcpXml} from './lib/fcpxml';
import {buildResolveEdl} from './lib/resolveEdl';
import {normalizeTranscriptWords} from './lib/transcription';
import supabase from './supabase/supabase';
import {transcribeVideo,transcribeCloudVideo} from './lib/transcriptionApi';
import {MAX_MEDIA_BYTES,MAX_MEDIA_DURATION,mediaLimitLabel} from './lib/resumableUpload';
const r2Enabled=typeof window!=='undefined'&&import.meta.env?.VITE_USE_R2_MEDIA!=='false';
import {MAX_BATCH_INTERVIEWS,validateBatchInterview,prepareBatchInterview} from './lib/batchImport';
import {PrecisePreview,playbackSegments} from './lib/precisePreview';
import {refineProjectTiming,restoreTranscriptRange} from './lib/cutTiming';
import './App.css';
import './components/AuthDialog.css';
import {workspaceRecoveryRequested,consumeWorkspaceRecovery} from './lib/workspaceStartup.js';

const storageKey='cutscript-demo-v1';
const recoveringView=()=>workspaceRecoveryRequested(window.location.search);
const MAX_FILE_BYTES=MAX_MEDIA_BYTES;
const MAX_DURATION_SECONDS=MAX_MEDIA_DURATION;
const initialProject=()=> {
  try {
    const saved=JSON.parse(localStorage.getItem(storageKey));
    if (Array.isArray(saved?.words) && !saved.isDemo && (saved.duration || saved.projectName))
      return {...demoProject,...saved,cuts:Array.isArray(saved.cuts)?saved.cuts:[],
       paperEdit:saved.paperEdit?{...saved.paperEdit,
        sources:saved.paperEdit.sources&&typeof saved.paperEdit.sources==='object'?saved.paperEdit.sources:{},
        blocks:Array.isArray(saved.paperEdit.blocks)?saved.paperEdit.blocks:[]}:undefined};
  } catch {
    // Ignore invalid local data.
  }
  return {...demoProject,title:'Untitled project',projectName:'Untitled project',words:[],cuts:[],duration:0,isDemo:false,frameStatus:'unverified'};
};

function App() {
  // ?recover=1 only redirects to Projects for the initial safe boot. Remove
  // the flag without navigating so subsequent refreshes restore this project's
  // last editor workspace (Paper Edit / Refine / Transcript).
  useEffect(()=>{
    try{consumeWorkspaceRecovery(window.location,window.history);}
    catch{/* Recovery still works even if the browser prevents URL rewriting. */}
  },[]);
  // A project opens with Files visible on desktop, while mobile starts with its
  // compact drawer closed. Within a project, manual collapse/expand is respected.
  const mobileFilesDefault=()=>Boolean(window.matchMedia?.('(max-width:650px)').matches);
  const [sidebarCollapsed,setSidebarCollapsed]=useState(mobileFilesDefault);
  const resetFilesOnProjectEntry=()=>setSidebarCollapsed(mobileFilesDefault());
  const [transcriptSplit,setTranscriptSplit]=useState(()=>{
    try{const value=Number(localStorage.getItem('cutscript.transcriptSplit'));return value>=35&&value<=65?value:55;}
    catch{return 55;}
  });
  const transcriptColumns=useRef(null),resizingTranscript=useRef(false);
  const changeTranscriptSplit=value=>setTranscriptSplit(Math.max(35,Math.min(65,value)));
  useEffect(()=>{try{localStorage.setItem('cutscript.transcriptSplit',String(transcriptSplit));}catch{/* Resizing still works. */}},[transcriptSplit]);
  const resizeTranscriptFromPointer=e=>{
    const rect=transcriptColumns.current?.getBoundingClientRect();
    if(rect)changeTranscriptSplit((e.clientX-rect.left)/rect.width*100);
  };
  const [theme,setTheme]=useState(()=>document.documentElement.dataset.theme==='dark'?'dark':'light');
  const toggleTheme=()=>setTheme(value=>value==='dark'?'light':'dark');
  const accountMenuRef=useRef(null);
  useEffect(()=>{
    const dismissOnOutsidePointer=event=>{
      const menu=accountMenuRef.current;
      if(menu?.open && !menu.contains(event.target))menu.open=false;
    };
    const dismissOnEscape=event=>{
      const menu=accountMenuRef.current;
      if(event.key!=='Escape' || !menu?.open)return;
      menu.open=false;
      menu.querySelector('summary')?.focus();
    };
    document.addEventListener('pointerdown',dismissOnOutsidePointer,true);
    document.addEventListener('keydown',dismissOnEscape);
    return()=>{
      document.removeEventListener('pointerdown',dismissOnOutsidePointer,true);
      document.removeEventListener('keydown',dismissOnEscape);
    };
  },[]);
  useEffect(()=>{document.documentElement.dataset.theme=theme;document.querySelector('meta[name="theme-color"]')?.setAttribute('content',theme==='light'?'#f9fbf9':'#151c22');try{localStorage.setItem('cutscript-theme',theme);}catch{/* Theme still works without storage. */}},[theme]);
  const [workspaceMode,setWorkspaceMode]=useState(()=>{
    try{return !recoveringView()&&['paper','transcript','refine'].includes(sessionStorage.getItem('cutscript-mode'))?sessionStorage.getItem('cutscript-mode'):'paper';}catch{return 'paper';}
  });
  useEffect(()=>{try{sessionStorage.setItem('cutscript-mode',workspaceMode);}catch{/* Keep mode usable without storage. */}},[workspaceMode]);
  // The public marketing site and the editor share a deployment but not UI.
  // Existing /app URLs, account recovery and embedded review routes keep working.
  const [marketingView,setMarketingView]=useState(()=>window.location.pathname==='/'&&!/(?:recover|code|token_hash|type=recovery)/i.test(window.location.search+window.location.hash));
  useEffect(()=>{
    const onPopState=()=>setMarketingView(window.location.pathname==='/'&&!/(?:recover|code|token_hash|type=recovery)/i.test(window.location.search+window.location.hash));
    window.addEventListener('popstate',onPopState);
    return()=>window.removeEventListener('popstate',onPopState);
  },[]);
  const [screen,setScreen]=useState(()=>{
    try{
      if(recoveringView())return 'projects';
       const billingReturn=new URLSearchParams(window.location.search).get('billing');
      if(['success','cancel','return'].includes(billingReturn))return 'account';
      return sessionStorage.getItem('cutscript-screen')==='editor'?'editor':'projects';
    }
    catch{return 'projects';}
  });
  useEffect(()=>{
    try{sessionStorage.setItem('cutscript-screen',screen);}catch{/* Navigation still works without storage. */}
  },[screen]);
  const [paperSourceId,setPaperSourceId]=useState('');
  const [importing,setImporting]=useState(false);
  const [processingId,setProcessingId]=useState(null);
  const importLock=useRef(false);
  const batchStop=useRef(false);
  const [batchReport,setBatchReport]=useState(null);
  const [batchStopping,setBatchStopping]=useState(false);
  const [project,setProject]=useState(initialProject);
  const [history,setHistory]=useState([]);
  const [future,setFuture]=useState([]);
  const [selected,setSelected]=useState([]);
  const [anchor,setAnchor]=useState(null);
  const [search,setSearch]=useState('');
  const [findSelectsOpen,setFindSelectsOpen]=useState(false);
  const findSelectsButtonRef=useRef(null);
  const [transcriptMeaningMatches,setTranscriptMeaningMatches]=useState([]);
  const [findSelectsNotice,setFindSelectsNotice]=useState('');
  const [sourcePriorityBusy,setSourcePriorityBusy]=useState(false);
  const aiAuditionEndRef=useRef(null);
  const [showRemoved,setShowRemoved]=useState(true);
  const [videoUrl,setVideoUrl]=useState('');
  const [uploadState,setUploadState]=useState('');
  const [transcribing,setTranscribing]=useState(false);
  const [paperSetup,setPaperSetup]=useState(null);
  const [paperExportKind,setPaperExportKind]=useState('xml');
  const [currentTime,setCurrentTime]=useState(0);
  const [playing,setPlaying]=useState(false);
  const [preparingAudio,setPreparingAudio]=useState(false);
  const [refiningCuts,setRefiningCuts]=useState(false);
  const refiningRef=useRef(false);
  const [previewMode,setPreviewMode]=useState('edited');
  const [reviewOpen,setReviewOpen]=useState(false);
  const [reviewAutoCreate,setReviewAutoCreate]=useState(false);
  const [reviewRefreshKey,setReviewRefreshKey]=useState(0);
  const [mediaLibraryOpen,setMediaLibraryOpen]=useState(false);
  const [cloudImportOpen,setCloudImportOpen]=useState(false);
  const [deleteProjectRow,setDeleteProjectRow]=useState(null);
  const [deleteProjectMode,setDeleteProjectMode]=useState('trash');
  const [mergeProjectRow,setMergeProjectRow]=useState(null);
  const [feedbackOpen,setFeedbackOpen]=useState(false);
  const [renamingInterviewId,setRenamingInterviewId]=useState(null);
  const [interviewNameDraft,setInterviewNameDraft]=useState('');
  const [savingInterviewName,setSavingInterviewName]=useState(false);
  const [exportOpen,setExportOpen]=useState(false);
  const [translationRequest,setTranslationRequest]=useState(null);
  const [authOpen,setAuthOpen]=useState(()=> {
    const query=new URLSearchParams(window.location.search);
    return query.get('recovery')==='1' || window.location.hash==='#/reset-password';
  });
  const [saveStatus,setSaveStatus]=useState('Saved locally');
  const [error,setError]=useState('');
  const {session,loading:authLoading,authError,clearAuthError,signIn,signUp,signOut,recoverySession,requestPasswordReset,updatePassword}=useSupabaseAuth();
  const videoRef=useRef(null);
  // Paper Edit uses its own video element: keep a single Space handler for both editors.
  const paperPlaybackRef=useRef(null);
  const fileRef=useRef(null);
  const urlRef=useRef('');
  const projectRef=useRef(project);
  const modeRef=useRef(previewMode);
  const sourceFileRef=useRef(null);
  const cloudAttemptRef=useRef(null);
  const reconnectRef=useRef(null);
  const activeClipRef=useRef(null);
  const audioRef=useRef(null);
  const playRequestRef=useRef(0);
  const sequenceEndedRef=useRef(false);
  const requestIdRef=useRef(0);
  const requestControllerRef=useRef(null);

  const resetWorkspace=(next,url='')=> {
    requestIdRef.current+=1;
    requestControllerRef.current?.abort();requestControllerRef.current=null;
    pauseEditing();audioRef.current?.dispose();audioRef.current=null;
    if(urlRef.current)URL.revokeObjectURL(urlRef.current);
    urlRef.current='';sourceFileRef.current=null;reconnectRef.current=null;
    setTranscribing(false);setProcessingId(null);setPlaying(false);setCurrentTime(0);setSelected([]);setAnchor(null);
    activeClipRef.current=null;sequenceEndedRef.current=false;
    setHistory([]);setFuture([]);setProject(next);setVideoUrl(url);setUploadState('');
  };
  const cloud=useCloudProjects({session,project,onOpen:resetWorkspace,onRename:name=>setProject(current=>({...current,projectName:name})),onClear:()=>resetWorkspace({...demoProject,words:[],cuts:[],isDemo:false,title:'Untitled project',duration:0}),onError:setError});

  const collectionId=project.collectionId || cloud.active?.id;
  const collectionRoot=cloud.rows.find(row=>row.id===collectionId);
  const collectionTitle=project.collectionId ? (collectionRoot?.data.projectName || collectionRoot?.data.title || 'Project') : (project.projectName || project.title);
  const collectionMedia=collectionId ? projectInterviews(cloud.rows,collectionId) : [];
  const colourSourceIds=sourceColourOrder(project.paperEdit?.sourceColourOrder,
    [...collectionMedia.map(row=>row.id),...Object.keys(project.paperEdit?.sources||{})]);
  // Restore the last Paper Edit source from this editor's browser, scoped to
  // their account and the current project, without modifying cloud project data.
  const interviewIds=collectionMedia.map(row=>row.id);
  const rememberedInterview=recallPaperInterview(
    typeof localStorage==='undefined'?null:localStorage,session?.user?.id,collectionId,interviewIds
  );
  const selectedInterviewId=resolvePaperInterview(paperSourceId,rememberedInterview,interviewIds,collectionId);
  // Only explicit, valid selections are stored. Initial hydration must NEVER
  // overwrite the remembered source with the root interview.
  useEffect(()=>{
    if(paperSourceId && interviewIds.includes(paperSourceId))
      storePaperInterview(localStorage,session?.user?.id,collectionId,paperSourceId);
  },[paperSourceId,collectionId,session?.user?.id,interviewIds.join('|')]);
  const workingRows=cloud.rows.map(row=>row.id===cloud.active?.id ? {...row,data:project} : row);
  const toolbarSourceId=workspaceMode==='transcript' ? cloud.active?.id : selectedInterviewId;
  const toolbarSource=workingRows.find(row=>row.id===toolbarSourceId);
  const aiSource=workspaceMode==='transcript' && toolbarSource?.media_ready && project.words?.length
    ? sourceSnapshot({...toolbarSource,data:project}) : null;
  const findSelectsSources=collectionId
    ?projectInterviews(workingRows,collectionId).filter(row=>row.media_ready&&row.data.words?.length)
      .map(row=>({...sourceSnapshot(row),speakerNames:row.data.speakerNames||{}}))
    :aiSource?[aiSource]:[];
  const aiRootId=project.collectionId || cloud.active?.id;
  const aiRootRow=workingRows.find(row=>row.id===aiRootId);
  const aiPaper=aiRootRow?.data.paperEdit || (!project.collectionId ? project.paperEdit : null) || {sources:{},blocks:[]};
  // Paper Edit may highlight a different interview from the active story.
  const sidebarInterview=workspaceMode!=='transcript'
    ? workingRows.find(row=>row.id===selectedInterviewId)
    : workingRows.find(row=>row.id===cloud.active?.id);
  const sidebarInterviewData=sidebarInterview?.data || project;
  const sidebarMediaAvailable=workspaceMode!=='transcript' ? Boolean(sidebarInterview?.media_ready) : Boolean(videoUrl);
  const locked=cloud.busy || transcribing || importing;
  const requestDeleteProject=row=>{
    if(locked)return;
    setError('');
    setDeleteProjectMode('trash');
    setDeleteProjectRow(row);
  };
  const requestPermanentDelete=row=>{
    if(locked)return;
    setError('');setDeleteProjectMode('permanent');setDeleteProjectRow(row);
  };
  const requestMerge=row=>{
    if(locked)return;
    setError('');setMergeProjectRow(row);
  };
  const confirmMerge=async(source,destination,appendAssembly)=>{
    if(locked)return false;
    const merged=await cloud.mergeProjects(source,destination,appendAssembly);
    if(merged){
      setMergeProjectRow(null);
      setPaperSourceId('');
      setScreen('projects');
      return merged;
    }
    return false;
  };
  const startInterviewRename=row=>{
    if(locked || savingInterviewName)return;
    setRenamingInterviewId(row.id);
    setInterviewNameDraft(interviewDisplayName(row.data));
  };
  const renameInterviewSpeaker=async(sourceId,speaker,label)=>{
    if(!sourceId||!speaker||!label?.trim())return;
    const row=workingRows.find(item=>item.id===sourceId);
    if(!row)return;
    const names={...(row.data.speakerNames||{}),[speaker]:label.trim().slice(0,60)};
    if(cloud.active?.id===sourceId){
      commit({...project,speakerNames:names});
    }else{
      try{await cloud.saveInterview(row,{...row.data,speakerNames:names});}
      catch(error){setError(error.message||'Could not save speaker name.');}
    }
  };
  const saveInterviewName=async row=>{
    const name=interviewNameDraft.trim();
    if(!name || !row || savingInterviewName)return;
    if(name===interviewDisplayName(row.data)){setRenamingInterviewId(null);return;}
    setSavingInterviewName(true);
    try{
      // Keep the immutable camera filename and exact XML timing metadata.
      if(row.id===cloud.active?.id){
        commit({...project,title:name,displayName:name,
          paperEdit:updateStorySourceLabel(project.paperEdit,row.id,name)});
      }else{
        await cloud.saveInterview(row,{...row.data,title:name,displayName:name});
        if(workspaceMode!=='transcript' && cloud.active?.id===collectionId){
          const revised=updateStorySourceLabel(project.paperEdit,row.id,name);
          if(revised!==project.paperEdit)commit({...project,paperEdit:revised});
        }
      }
      setRenamingInterviewId(null);
    }catch(e){setError(e.message || 'Could not rename this interview.');}
    finally{setSavingInterviewName(false);}
  };
  const openProject=async id=>{
    if(locked)return;
    setFindSelectsOpen(false);setPaperSourceId('');setError('');
    if(await cloud.open(id)){
      if(cloud.rows.find(row=>row.id===id)?.source_detached)setWorkspaceMode('paper');
      resetFilesOnProjectEntry();
      setScreen('editor');
    }
  };
  const selectInterview=async id=>{
    if(locked)return;
    setFindSelectsOpen(false);setTranscriptMeaningMatches([]);setFindSelectsNotice('');
    setPaperSourceId(id);
    if(workspaceMode==='transcript')await cloud.open(id,{interview:true,quiet:true}).then(ok=>{if(ok)setWorkspaceMode('transcript');});
  };
  const openTranscript=async(id=selectedInterviewId)=>{
    if(locked)return;
    setFindSelectsOpen(false);setTranscriptMeaningMatches([]);
    pauseEditing();
    if(id && id!==cloud.active?.id){if(!await cloud.open(id,{interview:true,quiet:true}))return;}
    setWorkspaceMode('transcript');
  };
  const openPaper=async()=>{
    if(locked)return;
    setFindSelectsOpen(false);setTranscriptMeaningMatches([]);
    if(workspaceMode==='transcript')setPaperSourceId(cloud.active?.id || '');
    pauseEditing();setSelected([]);
    if(project.collectionId && cloud.active?.id!==project.collectionId){
      if(!await cloud.open(project.collectionId,{quiet:true}))return false;
    }
    // A stale preview fetch error must not be mistaken for a failed cloud save.
    if(cloud.status!=='Cloud save failed')setError(current=>current==='Failed to fetch'?'':current);
    setFeedbackOpen(false);setWorkspaceMode('paper');return true;
  };

  const openRefine=async()=>{
    // Refine adjusts collected Assembly passages, never a blank timeline.
    // Keep the empty-state component as a safety fallback for deleted passages.
    if(locked||!project.paperEdit?.blocks?.length)return;
    if(workspaceMode==='transcript'){
      const ok=await openPaper();
      if(!ok)return;
    }else{
      pauseEditing();setSelected([]);setFeedbackOpen(false);setFindSelectsOpen(false);
      if(project.collectionId&&cloud.active?.id!==project.collectionId){
        if(!await cloud.open(project.collectionId,{quiet:true}))return;
      }
    }
    setWorkspaceMode('refine');
  };
  const confirmDeleteProject=async(row,{deleteMedia=false,mode='trash'}={})=>{
    if(locked)return false;
    const ok=await cloud.remove(row,{deleteMedia,confirmed:true,mode});
    if(ok){setDeleteProjectRow(null);setPaperSourceId('');setScreen('projects');}
    return ok;
  };

  const deleteInterview=async row=>{
    if(locked)return;
    if(!row.data.collectionId){
      const removed=await cloud.detachRootInterview(row);
      if(removed){setPaperSourceId('');setWorkspaceMode('paper');}
      return;
    }
    const wasActive=cloud.active?.id===row.id;
    const wasSelected=selectedInterviewId===row.id;
    const rootId=row.data.collectionId || row.id;
    const removed=await cloud.remove(row);
    if(!removed)return;
    if(wasSelected)setPaperSourceId(rootId);
    if(wasActive){
      // Return to the project Assembly rather than leaving an empty deleted file open.
      if(await cloud.open(rootId))setWorkspaceMode('paper');
      else setScreen('projects');
    }
  };

  projectRef.current=project;
  modeRef.current=previewMode;

  useEffect(()=> {
    const file=sourceFileRef.current;
    if(!session || cloud.busy || cloud.active || !file || !project.duration || reconnectRef.current || cloudAttemptRef.current===file)return;
    cloudAttemptRef.current=file;
    cloud.upload(file,p=>setUploadState('Saving video · '+p+'%'));
  },[session?.user?.id,project,cloud.busy,cloud.active]);

  useEffect(()=> {
    const warn=event=>{
      if(importing || (sourceFileRef.current && !cloud.active)){event.preventDefault();event.returnValue='';}
    };
    window.addEventListener('beforeunload',warn);
    return()=>window.removeEventListener('beforeunload',warn);
  },[cloud.active,importing]);

  const recoveryRoute=authOpen && (
    new URLSearchParams(window.location.search).get('recovery')==='1' ||
    window.location.hash==='#/reset-password'
  );

  useEffect(()=> {
    if (recoverySession) setAuthOpen(true);
  },[recoverySession]);

  useEffect(()=> {
    if(cloud.active){localStorage.removeItem(storageKey);return;}
    setSaveStatus('Saving locally…');
    const timer=setTimeout(()=> {
      try {
        localStorage.setItem(storageKey,JSON.stringify(project));
        setSaveStatus('Saved locally');
      } catch {
        setSaveStatus('Local save failed');
      }
    },350);
    return ()=> clearTimeout(timer);
  },[project,cloud.active]);

  useEffect(()=> ()=> {
    requestControllerRef.current?.abort();
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    playRequestRef.current++;audioRef.current?.dispose();
  },[]);

  const pauseEditing=()=> { playRequestRef.current++;audioRef.current?.stop();aiAuditionEndRef.current=null;setPreparingAudio(false);videoRef.current?.pause(); setPlaying(false); activeClipRef.current=null; };

  const commit=next=> {
    pauseEditing();
    // Keep the active cut's lightweight snapshot in sync after every edit.
    // Older projects with no alternate cuts are unchanged until first use.
    const updated=next?.paperEdit?.assemblyCuts
      ?{...next,paperEdit:syncActiveAssemblyCut(next.paperEdit)}:next;
    setHistory(previous=> [...previous.slice(-49),project]);
    setFuture([]);
    setProject(updated);
  };

  const undo=()=> {
    if (!history.length) return;
    pauseEditing();
    setFuture(previous=> [project,...previous]);
    setProject(history.at(-1));
    setHistory(previous=> previous.slice(0,-1));
  };

  const redo=()=> {
    if (!future.length) return;
    pauseEditing();
    setHistory(previous=> [...previous,project]);
    setProject(future[0]);
    setFuture(previous=> previous.slice(1));
  };

  const seek=time=> {
    pauseEditing();
    sequenceEndedRef.current=false;
    const position=clamp(time,0,project.duration);
    activeClipRef.current=sequenceClips(projectRef.current).find(part=>position>=part.start && position<part.end)?.id ?? null;
    setCurrentTime(position);
    if (videoRef.current && videoRef.current.readyState) videoRef.current.currentTime=position;
  };

  const selectWord=(word,shift,toggle)=> {
    pauseEditing();
    const index=project.words.findIndex(item=> item.id===word.id);
    if (shift && anchor !==null) {
      setSelected(project.words.slice(Math.min(anchor,index),Math.max(anchor,index)+1).map(item=> item.id));
    } else if(toggle){
      setSelected(previous=>previous.includes(word.id) ? previous.filter(id=>id!==word.id) : [...previous,word.id]);
      setAnchor(index);
    } else {
      setSelected([word.id]);
      setAnchor(index);
    }
    seek(word.start);
  };

  const prepareSourceAudio=async()=> {
    if(!audioRef.current){
      const AudioContext=window.AudioContext || window.webkitAudioContext;
      if(!AudioContext)throw new Error('This browser does not support audio analysis. Try Chrome or Edge.');
      audioRef.current=new PrecisePreview(new AudioContext());
    }
    const engine=audioRef.current;
    await engine.prepare(videoUrl,project.duration);
    return engine.buffer;
  };

  const refineTiming=async(removeSelection=false)=> {
    if(refiningRef.current || transcribing || cloud.busy)return;
    const snapshot=project;
    const selectedIds=[...selected];
    if(!videoUrl && !project.isDemo){setError('Open the source video to refine cut timing.');return;}
    pauseEditing();setError('');
    refiningRef.current=true;setRefiningCuts(true);
    try{
      let next=snapshot;
      if(removeSelection){
        const ranges=selectedRanges(next.words,selectedIds,word=>!isRemoved(word,next.cuts,next.duration));
        if(!ranges.length)return;
        for(const range of ranges)next=removeSourceRange(next,range.start,range.end);
      }
      if(!snapshot.isDemo && (acousticBoundariesEnabled || snapshot.alignment!=='elevenlabs')){
        const buffer=await prepareSourceAudio();
        if(projectRef.current!==snapshot || !buffer)return;
        next=refineProjectTiming(next,buffer,{experimental:acousticBoundariesEnabled});
      }
      if(projectRef.current!==snapshot)return;
      commit(next);setSelected([]);
    }catch(e){if(projectRef.current===snapshot)setError(`Cut timing was not changed. ${e.message}`);}
    finally{refiningRef.current=false;setRefiningCuts(false);}
  };

  const remove=()=>{setShowRemoved(false);return refineTiming(true);};
  const omit=()=>{setShowRemoved(true);return refineTiming(true);};

  const restore=()=> {
    const ranges=selectedRanges(project.words,selected);
    if(!ranges.length)return;
    let next=project;
    for(const range of ranges)next=restoreTranscriptRange(next,range.start,range.end);
    commit(next);
    setSelected([]);
  };

  const correct=()=> {
    if (selected.length!==1) {
      setError('Select one word to correct its display text.');
      return;
    }
    const word=project.words.find(item=> item.id===selected[0]);
    const text=window.prompt('Correct transcript text (source timing will not change):',word.text);
    if (text===null || !text.trim()) return;
    commit({...project,words: project.words.map(item=> item.id===word.id ? {...item,text: text.trim()} : item)});
  };

  const loadFile=async file=> {
    if(locked || importLock.current || !file)return;
    const extension=file.name.toLowerCase().match(/\.[^.]+$/)?.[0];
    const expectedType=extension==='.mp4' ? 'video/mp4' : extension==='.webm' ? 'video/webm' : '';
    if(!expectedType || (file.type && file.type!==expectedType)){setError('Choose an MP4 video with an audio track.');return;}
    if(file.size>PROXY_MAX_SOURCE_BYTES){setError('This original file is too large for reliable browser conversion. Export a 480p H.264/AAC proxy yourself, without changing the frame rate.');return;}
    importLock.current=true;setImporting(true);setError('');setBatchReport(null);setUploadState('Reading video…');
    try {
      // Read only MP4 metadata boxes; never materialise multi-GB originals as ArrayBuffers.
      const [metadata,detected]=await Promise.all([readVideoMetadata(file),extension==='.mp4' ? inspectMp4TimingFile(file) : null]);
      if(metadata.duration>MAX_DURATION_SECONDS)throw new Error('The beta currently supports interviews up to four hours.');
      if(await hasInterviewAudioTrack(file)===false)
        throw new Error('No audio track was found in this video. Export it with interview audio enabled before uploading.');
      let uploadFile=file,proxy=null;
      if(!r2Enabled&&shouldOfferProxy(file,metadata,MAX_FILE_BYTES)){
        // Never reject an original that fits the configured upload limit
        // merely because the estimated optimised version would be larger.
        const expected=estimateProxyBytes(metadata.duration);
        if(expected>MAX_FILE_BYTES && file.size>MAX_FILE_BYTES)
          throw new Error(`Even an optimised 480p review copy may exceed the current ${mediaLimitLabel(MAX_FILE_BYTES)} upload limit. Export a smaller H.264/AAC proxy at the original frame rate.`);
        if(expected<=MAX_FILE_BYTES && (file.size>MAX_FILE_BYTES || expected<file.size*0.85)){
          setUploadState('Preparing 480p proxy locally · original stays on your computer…');
          try{
            const converted=await createReviewProxy(file,{
              metadata,sourceTiming:detected,maxBytes:MAX_FILE_BYTES,
              onProgress:percent=>setUploadState('Preparing 480p proxy · '+percent+'%')
            });
            uploadFile=converted.file;proxy=converted.proxy;
            setUploadState('Proxy verified · original frame rate and audio timing retained');
          }catch(error){
            // Uploading the ORIGINAL is safe if it already fits Storage. A failed
            // conversion must never cause an unverified proxy to be uploaded.
            if(file.size>MAX_FILE_BYTES)throw error;
            setUploadState('Proxy conversion unavailable · uploading unchanged original');
          }
        }else{
          setUploadState('Original already compressed efficiently · uploading unchanged');
        }
      }
      if(uploadFile.size>MAX_FILE_BYTES)
        throw new Error(`The review video exceeds the current ${mediaLimitLabel(MAX_FILE_BYTES)} upload limit. Supply a smaller proxy without changing the source frame rate.`);
      await cloud.flush();
      // File metadata and source timecode always refer to the camera ORIGINAL,
      // not the resized web preview. XML relinks to the original filename.
      const next={...demoProject,title:file.name.replace(/\.[^.]+$/,''),filename:file.name,...metadata,uploadModified:file.lastModified,words:[],cuts:[],isDemo:false,frameStatus:'unverified',...(detected || {}),...(proxy?{proxy}:{})};
      if(collectionId && session){
        // The root remains the owner of the story even when adding from cleanup.
        if(project.collectionId && !await cloud.open(collectionId))return;
        setUploadState(`Uploading ${file.name}…`);
        const row=await cloud.addInterview(uploadFile,{...next,collectionId},p=>setUploadState('Uploading interview · '+p+'%'));
        if(row){setPaperSourceId(row.id);setWorkspaceMode('paper');setUploadState('Interview saved · ready to transcribe');}
        return;
      }
      const reconnect=!cloud.active && !videoUrl && !project.isDemo && project.words.length>0 && file.name===project.filename;
      if(reconnect && (Math.abs(metadata.duration-project.duration)>0.1 || metadata.width!==project.width || metadata.height!==project.height))throw new Error('This video does not match the saved transcript. Choose its original source file.');
      if(!reconnect && !cloud.active && !project.isDemo && project.words.length && !window.confirm('Replace this unsaved local interview? Sign in first to preserve it and add another interview.'))return;
      pauseEditing();audioRef.current?.dispose();audioRef.current=null;
      cloud.detach();setHistory([]);setFuture([]);setPaperSourceId('');
      if(urlRef.current)URL.revokeObjectURL(urlRef.current);
      const url=URL.createObjectURL(uploadFile);urlRef.current=url;sourceFileRef.current=uploadFile;reconnectRef.current=null;
      setProject(reconnect ? {...project,...metadata} : {...next,projectName:!project.isDemo && project.projectName ? project.projectName : next.title});
      setVideoUrl(url);setWorkspaceMode('paper');setCurrentTime(0);setSelected([]);
      setUploadState(session?'Saving interview…':'Video ready · sign in to save and transcribe');
    }catch(e){setError(e.message);setUploadState('Import failed · try again');}
    finally{importLock.current=false;setImporting(false);}
  };

  const importBatch=async selectedFiles=>{
    const files=Array.from(selectedFiles||[]);
    if(!files.length)return;
    if(!session){
      setError('Sign in before importing multiple interviews and starting transcription.');
      setAuthOpen(true);return;
    }
    if(locked||importLock.current)return;
    if(files.length>MAX_BATCH_INTERVIEWS){
      setError(`Select up to ${MAX_BATCH_INTERVIEWS} interviews per batch (the current beta transcription hourly limit).`);
      return;
    }
    importLock.current=true;batchStop.current=false;setBatchStopping(false);
    setImporting(true);setTranscribing(true);setError('');
    const statuses=files.map(file=>({name:file.name,status:'queued',detail:'Waiting to validate'}));
    const update=(index,status,detail)=>{
      statuses[index]={...statuses[index],status,detail};
      setBatchReport({active:true,items:statuses.map(item=>({...item}))});
      setUploadState(`${index+1}/${files.length} · ${files[index].name} · ${detail}`);
    };
    setBatchReport({active:true,items:statuses.map(item=>({...item}))});
    let uploaded=0,transcribed=0;
    try{
      const prepared=[];
      // Preflight every source before any network upload. An invalid file
      // remains rejected, while usable neighbours can still complete.
      for(let i=0;i<files.length;i++){
        if(batchStop.current)break;
        const file=files[i];
        try{
          update(i,'checking','Checking type, duration and upload size');
          const extension=file.name.toLowerCase().match(/\.[^.]+$/)?.[0];
          if(!['.mp4','.webm'].includes(extension)||file.size>PROXY_MAX_SOURCE_BYTES){
            validateBatchInterview(file,{duration:1,width:1,height:1});
          }
          const metadata=await readVideoMetadata(file);
          validateBatchInterview(file,metadata);
          if(await hasInterviewAudioTrack(file)===false)
            throw new Error('No audio track was found. Export the interview with audio included and try again.');
          const detected=extension==='.mp4'?await inspectMp4TimingFile(file):null;
          prepared.push({file,metadata,detected,i});
          update(i,'ready','Validated · ready for upload');
        }catch(error){update(i,'rejected',error.message||'Unsupported interview');}
      }
      if(!prepared.length)return;
      await cloud.flush();
      let rootId=collectionId||null;
      if(rootId && cloud.active?.id!==rootId){
        if(!await cloud.open(rootId))throw new Error('Could not open the project for batch import.');
      }
      for(const {file,metadata,detected,i} of prepared){
        if(batchStop.current){update(i,'stopped','Stopped before upload');continue;}
        let row;
        try{
          update(i,'preparing','Preparing a safe review copy');
          const {uploadFile,proxy}=await prepareBatchInterview(file,metadata,detected,
            message=>update(i,'preparing',message));
          const data={...demoProject,title:file.name.replace(/\.[^.]+$/,''),
            filename:file.name,...metadata,uploadModified:file.lastModified,
            words:[],cuts:[],isDemo:false,frameStatus:'unverified',
            ...(detected||{}),...(proxy?{proxy}:{})};
          update(i,'uploading','Uploading to private storage');
          if(!rootId){
            // The first upload becomes the project owner and Assembly root.
            const initial={...data,projectName:project.projectName&&project.projectName!=='Untitled project'
              ?project.projectName:data.title};
            row=await cloud.createBatchRoot(uploadFile,initial,
              percent=>update(i,'uploading',`Uploading · ${percent}%`));
            rootId=row.id;
          }else{
            row=await cloud.addInterview(uploadFile,{...data,collectionId:rootId},
              percent=>update(i,'uploading',`Uploading · ${percent}%`));
          }
          if(!row)throw new Error('Upload was interrupted. Reopen the project to retry.');
          uploaded+=1;
          if(uploaded===1){setPaperSourceId(row.id);setWorkspaceMode('paper');}
        }catch(error){
          update(i,'failed',`Upload failed: ${error.message||'Unknown error'}`);
          continue;
        }
        try{
          setProcessingId(row.id);
          update(i,'transcribing','Automatically transcribing…');
          const result=await transcribeCloudVideo(row.id,{
            getAccessToken:getMeaningToken,
            onState:state=>update(i,'transcribing',/safe to (leave|return)/i.test(state)?'Transcribing · keep this tab open for the remaining batch':state)
          });
          const words=normalizeTranscriptWords(result.words,metadata.duration);
          await cloud.saveBatchInterview(row,{
            ...row.data,words,cuts:[],clips:undefined,cutRefinements:[],
            alignment:result.alignment,isDemo:false,transcriptionError:null
          });
          transcribed+=1;
          update(i,'complete',`${words.length.toLocaleString()} words transcribed${result.warning?' · '+result.warning:''}`);
        }catch(error){
          const message=error.message||'Transcription could not be completed. Check the video audio and retry.';
          update(i,'failed',message);
          // Keep the failed file selected so its actionable explanation appears
          // in the full-size transcript workspace, not just the tiny file list.
          setPaperSourceId(row.id);setWorkspaceMode('paper');
          try{
            await cloud.saveBatchInterview(row,{...row.data,transcriptionError:message});
          }catch{
            setError('Could not save the failed transcription status. The uploaded video is still in your project.');
          }
        }finally{setProcessingId(null);}
      }
      setUploadState(`Batch finished · ${uploaded} uploaded · ${transcribed} transcribed`);
    }catch(error){
      setError(error.message||'Batch import interrupted.');
      setUploadState('Batch import interrupted');
    }finally{
      for(let i=0;i<statuses.length;i++)if(['queued','ready','checking','preparing'].includes(statuses[i].status)){
        statuses[i]={...statuses[i],status:'stopped',detail:'Not uploaded'};
      }
      setBatchReport({active:false,items:statuses.map(item=>({...item}))});
      setProcessingId(null);setImporting(false);setTranscribing(false);
      importLock.current=false;batchStop.current=false;setBatchStopping(false);
    }
  };

  const onMetadata=()=> {
    if(cloud.active)return;
    const video=videoRef.current;
    if (!video || !Number.isFinite(video.duration)) {
      setError('Could not read video metadata. Try another browser-playable file.');
      setUploadState('Failed to read video metadata');
      return;
    }
    const prior=reconnectRef.current;
    reconnectRef.current=null;
    if(prior && (Math.abs(prior.duration-video.duration)>0.1 || (prior.width && prior.width!==video.videoWidth) || (prior.height && prior.height!==video.videoHeight))){
      sourceFileRef.current=null;setVideoUrl('');
      setError('This file does not match the saved transcript duration or dimensions. Choose the original video. Your transcript and edits are unchanged.');
      return;
    }
    setProject(previous=> previous.proxy?.verified ? previous : ({...previous,duration:video.duration,width:video.videoWidth,height:video.videoHeight}));
    setUploadState(video.duration>MAX_DURATION_SECONDS ? 'Preview ready · exceeds four-hour transcription limit.' : 'Local preview ready');
  };

  const transcribe=async (targetId)=> {
    if(typeof targetId==='string'){
      const chosen=cloud.rows.find(item=>item.id===targetId);
      if(!chosen){setError('This interview could not be found. Refresh the project.');return;}
      const hasTranscript=Boolean(chosen.data.words?.length);
      if(hasTranscript && !window.confirm(`Re-transcribe "${chosen.data.title || chosen.data.filename || 'this interview'}"?\n\nThis replaces the current transcript and resets its word-level cuts and timing. Existing paper-edit selections may need review afterward.`))return;
    }
    if(!session){setAuthOpen(true);return;}
    if(requestControllerRef.current || locked)return;
    const id=typeof targetId==='string' ? targetId : cloud.active?.id;
    const row=cloud.rows.find(item=>item.id===id);
    const isActive=!id || id===cloud.active?.id;
    const target=isActive ? projectRef.current : row?.data;
    if(!target || !target.duration || target.duration>MAX_DURATION_SECONDS){setError('Choose an interview up to four hours.');return;}
    const requestId=requestIdRef.current,controller=new AbortController();
    requestControllerRef.current=controller;setProcessingId(id || 'local');setTranscribing(true);setError('');setUploadState('Loading interview…');
    try {
      await cloud.flush();
      const stored=row || cloud.active;
      const result=stored?.media_ready
        ? await transcribeCloudVideo(stored.id,{signal:controller.signal,getAccessToken:getMeaningToken,onState:setUploadState,force:Boolean(target.words?.length)})
        : await (async()=>{
            const file=sourceFileRef.current;
            if(!file||file.size>24_000_000)throw new Error('Finish saving this video to the cloud before transcription.');
            return transcribeVideo(file,{signal:controller.signal,getAccessToken:getMeaningToken,onState:setUploadState});
          })();
      if(requestId!==requestIdRef.current)return;
      const words=normalizeTranscriptWords(result.words,target.duration);
      const next={...(isActive ? projectRef.current : target),words,cuts:[],clips:undefined,cutRefinements:[],alignment:result.alignment,isDemo:false,transcriptionError:null};
      if(isActive){commit(next);setSelected([]);setShowRemoved(false);}
      else await cloud.saveInterview(row,next);
      setUploadState(`Transcript ready · ${words.length} words`);
      if(result.warning)setError(result.warning);
    }catch(e){
      if(e.name!=='AbortError' && requestId===requestIdRef.current){
        const message=e.message || 'The transcription could not be completed. Check the audio and retry.';
        setError(message);setUploadState('Transcription failed · retry this interview');
        if(row?.media_ready){
          try{
            await cloud.saveBatchInterview(row,{...(isActive?projectRef.current:row.data),transcriptionError:message});
          }catch{
            setError(message+' Your video is saved, but the failure status could not be saved.');
          }
        }
      }
    }finally{
      if(requestId===requestIdRef.current){requestControllerRef.current=null;setTranscribing(false);setProcessingId(null);}
    }
  };

  const play=async()=> {
    const video=videoRef.current;
    if(!video || refiningRef.current)return;
    if(playing || preparingAudio){pauseEditing();return;}
    const ticket=++playRequestRef.current;
    try{
      if(previewMode==='edited'){
        const intervals=sequenceClips(project);
        if(!intervals.length)return;
        if(!audioRef.current){
          const AudioContext=window.AudioContext || window.webkitAudioContext;
          if(!AudioContext)throw new Error('This browser does not support precise audio preview. Try Chrome or Edge.');
          audioRef.current=new PrecisePreview(new AudioContext());
        }
        setPreparingAudio(true);
        await audioRef.current.prepare(videoUrl,project.duration);
        if(ticket!==playRequestRef.current)return;
        video.muted=true;
        // Seek to the *first retained frame* before starting playback. Previously
        // playing first, then seeking, briefly showed the old frame (a flash).
        const startingSegment=playbackSegments(intervals,currentTime,sequenceEndedRef.current)[0];
        if(!startingSegment)return;
        if(Math.abs(video.currentTime-startingSegment.start)>0.025)video.currentTime=startingSegment.start;
        await video.play();
        if(ticket!==playRequestRef.current){video.pause();return;}
        audioRef.current.start(intervals,currentTime,sequenceEndedRef.current);
        const position=audioRef.current.position();
        activeClipRef.current=position.id;video.currentTime=position.source;setCurrentTime(position.source);
        sequenceEndedRef.current=false;
      }else{
        video.muted=false;await video.play();
        if(ticket!==playRequestRef.current){video.pause();return;}
      }
      setPlaying(true);
    }catch(e){if(ticket===playRequestRef.current){pauseEditing();setError(e.message || 'Could not prepare precise preview.');}}
    finally{if(ticket===playRequestRef.current)setPreparingAudio(false);}
  };

  const onTimeUpdate=()=> {
    const video=videoRef.current;
    if(!video)return;
    if(modeRef.current==='edited'){
      const position=audioRef.current?.position();
      if(!position)return;
      setCurrentTime(position.source);
      if(position.ended){
        pauseEditing();sequenceEndedRef.current=true;
        activeClipRef.current=position.id;video.currentTime=position.source;return;
      }
      if(!video.seeking && (activeClipRef.current!==position.id || Math.abs(video.currentTime-position.source)>0.12))video.currentTime=position.source;
      activeClipRef.current=position.id;
      if(video.paused)video.play().catch(()=>{pauseEditing();setError('Video playback failed.');});
    }else{
      const sourceTime=video.currentTime;
      setCurrentTime(sourceTime);
      if(aiAuditionEndRef.current!==null && sourceTime>=aiAuditionEndRef.current-0.02){
        pauseEditing();
      }
    }
  };
  const onEnded=()=> {if(modeRef.current==='edited')onTimeUpdate();else setPlaying(false);};

  const findSelectsAudioRef=useRef(null),findSelectsListenTicket=useRef(0),findSelectsEndRef=useRef(null);
  const stopFoundTake=()=>{
    const ended=findSelectsEndRef.current;findSelectsEndRef.current=null;ended?.();
    findSelectsListenTicket.current++;
    if(findSelectsAudioRef.current){
      findSelectsAudioRef.current.pause();
      findSelectsAudioRef.current.removeAttribute('src');
      findSelectsAudioRef.current=null;
    }
  };
  const previewFoundTake=async(match,onEnd)=>{
    stopFoundTake();
    pauseEditing();
    const ticket=findSelectsListenTicket.current;
    const row=workingRows.find(item=>item.id===match.sourceId&&item.media_ready);
    if(!row||!row.data.words?.length)throw new Error('Source interview is unavailable.');
    const valid=match.words?.length&&match.words.every(w=>row.data.words.some(word=>word.id===w.id));
    if(!valid)throw new Error('This interview transcript changed. Search again.');
    const signed=await cloud.url(row);
    if(ticket!==findSelectsListenTicket.current)return;
    if(!signed)throw new Error('Source media could not be loaded.');
    const audio=new Audio();
    findSelectsAudioRef.current=audio;audio.preload='auto';
    const start=match.words[0].start,end=match.words.at(-1).end;
    const stopAtEnd=()=>{if(audio.currentTime>=end-.025&&ticket===findSelectsListenTicket.current)stopFoundTake();};
    audio.addEventListener('timeupdate',stopAtEnd);
    audio.addEventListener('ended',stopAtEnd);
    audio.src=signed;
    await new Promise((resolve,reject)=>{
      if(audio.readyState>=1){resolve();return;}
      audio.addEventListener('loadedmetadata',resolve,{once:true});
      audio.addEventListener('error',()=>reject(new Error('Could not decode the interview audio.')),{once:true});
    });
    if(ticket!==findSelectsListenTicket.current)return;
    audio.currentTime=start;
    findSelectsEndRef.current=onEnd;
    try{await audio.play();}
    catch(e){stopFoundTake();throw new Error('Could not play this interview: '+e.message);}
  };
  const previewAiWords=words=>{
    if(!words?.length)return;
    const video=videoRef.current;
    if(!video){setError('Open the interview video before previewing a select.');return;}
    const start=words[0].start,end=words.at(-1).end;
    seek(start);
    setSelected(words.map(word=>word.id));
    setPreviewMode('source');modeRef.current='source';
    aiAuditionEndRef.current=end;
    video.muted=false;
    video.play().then(()=>setPlaying(true)).catch(e=>{pauseEditing();setError(e.message || 'Could not preview selected words.');});
  };
  // Both transcript views share editorialTags on the project root, so an
  // interview's decisions survive switching editing modes.
  // Both Transcript and Paper Edit edit the SAME interview source.
  // The story Assembly is intentionally unchanged by source cleanup.
  const applyInterviewCleanup=async(sourceId,suggestions,expectedWordIds,expectedSignature)=>{
    if(locked)throw new Error('Finish the current operation before applying cleanup.');
    if(!suggestions.length)return;
    const row=workingRows.find(item=>item.id===sourceId);
    const current=row?.data || (sourceId==='local'&&!cloud.active?project:null);
    if(!current?.words?.length)throw new Error('The interview is unavailable. Reopen and retry.');
    if(cleanupSignature(current)!==expectedSignature)
      throw new Error('The source interview changed during cleanup. Run it again.');
    const priorities=sourceWordPriority(aiPaper,{id:sourceId,words:current.words});
    const used=storyWordIds(current.words,aiPaper.blocks||[],sourceId);
    const protectedRanges=cleanupProtectedRanges(current.words,priorities,used,current.cuts||[],current.duration);
    const approved=safeCleanupSuggestions(suggestions,current.words,protectedRanges);
    if(approved.length!==suggestions.length)
      throw new Error('A suggestion now overlaps protected footage. Run AI Cleanup again.');
    const updated=applyCleanupToInterview(current,approved,expectedWordIds);
    if(sourceId===cloud.active?.id||(sourceId==='local'&&!cloud.active)){
      commit(updated);
    }else{
      if(!row)throw new Error('This interview is not available.');
      await cloud.flush();
      await cloud.saveInterview(row,updated);
    }
  };
  const cycleTranscriptSourcePriority=async priority=>{
    if(locked||sourcePriorityBusy||!selected.length||!aiSource||!aiRootId)return;
    setSourcePriorityBusy(true);
    try{
      await cloud.flush();
      const nextPaper=markSourceWords(aiPaper,aiSource,selected,priority);
      if(aiRootId===cloud.active?.id){
        commit({...project,paperEdit:nextPaper});
      }else{
        if(!aiRootRow)throw new Error('Project edit could not be found.');
        await cloud.saveInterview(aiRootRow,{...aiRootRow.data,paperEdit:nextPaper});
      }
    }catch(error){setError('Could not save source priority: '+(error.message||'Try again.'));}
    finally{setSourcePriorityBusy(false);}
  };
  const addAiWordsToStory=async match=>{
    if(!match?.words?.length||!aiRootId||locked)throw new Error('The editor is busy.');
    const mediaRow=workingRows.find(item=>item.id===match.sourceId&&item.media_ready&&item.data.words?.length);
    if(!mediaRow)throw new Error('The interview for this select is unavailable.');
    const chosen=sourceSnapshot(mediaRow);
    if(!match.words.every(word=>chosen.words.some(w=>w.id===word.id)))
      throw new Error('This source transcript changed. Search again.');
    primePaperPaste();
    setFindSelectsNotice('Adding passage to edit…');
    try{
      await cloud.flush();
      const nextPaper=addPassages(aiPaper,chosen,match.words.map(word=>word.id));
      if(nextPaper===aiPaper || nextPaper.blocks.length<=aiPaper.blocks.length)throw new Error('No new passage to add.');
      if(aiRootId===cloud.active?.id){
        commit({...project,paperEdit:nextPaper});
      }else{
        if(!aiRootRow)throw new Error('Project edit could not be found.');
        await cloud.saveInterview(aiRootRow,{...aiRootRow.data,paperEdit:nextPaper});
      }
      playPaperPaste();
      setFindSelectsNotice('Passage added to your edit.');
    }catch(e){setFindSelectsNotice('Add to story failed.');throw e;}
  };


  useEffect(()=> {
    if (!playing) return;
    let frame;
    const tick=()=> { onTimeUpdate(); frame=requestAnimationFrame(tick); };
    frame=requestAnimationFrame(tick);
    return ()=>cancelAnimationFrame(frame);
  },[playing,project,previewMode]);

  useEffect(()=> {
    const handler=event=> {
      const target=event.target;
      if (screen!=='editor' || refiningRef.current || event.defaultPrevented || event.isComposing || target?.closest?.('input,textarea,select,[contenteditable="true"],[role="textbox"]') || authOpen || exportOpen || reviewOpen || cloud.busy) return;
      if (['Backspace','Delete'].includes(event.key) && !event.ctrlKey && !event.metaKey && !event.altKey && selected.length && workspaceMode!=='paper') {
        event.preventDefault();
        if (!event.repeat && !transcribing) remove();
        return;
      }
      if(event.code==='Space' && !event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey) {
        // Prevent the browser scrolling/activating focused UI while playing.
        // Transcript text handles its own Space key and stops propagation.
        if(event.target?.closest?.('button,a,[role="button"]:not([data-word-id])'))return;
        event.preventDefault();
        if(!event.repeat){
          if(workspaceMode==='paper'||workspaceMode==='refine')paperPlaybackRef.current?.();
          else play();
        }
        return;
      }
      if((workspaceMode==='paper'||workspaceMode==='refine') && !(event.metaKey || event.ctrlKey))return;
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase()==='z') {
        event.preventDefault();
        event.shiftKey ? redo() : undo();
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase()==='f') {
        event.preventDefault();
        const searchInput=document.querySelector('.search input');
        if(searchInput)searchInput.focus();else document.querySelector('[data-transcript-search]')?.click();
      }
    };
    window.addEventListener('keydown',handler);
    return ()=> window.removeEventListener('keydown',handler);
  });

  useEffect(()=>{const hide=()=>{if(document.hidden)pauseEditing();};document.addEventListener('visibilitychange',hide);return()=>document.removeEventListener('visibilitychange',hide);},[]);

  const audioExportController=useRef(null);
  const [audioExportStatus,setAudioExportStatus]=useState(null);
  const audioExportRunning=Boolean(audioExportStatus);
  const cancelAudioExport=()=>audioExportController.current?.abort();
  const exportMp3=async(source,interviewId)=>{
   if(audioExportController.current)return;
   const controller=new AbortController();
   audioExportController.current=controller;
   setAudioExportStatus({percent:0,passage:0,count:0});
   setError('');
   try{
    const {makeAudioRoughCutPlan,renderAudioRoughCut,roughCutFileName}=await import('./lib/audioRoughCut.js');
    const row=workingRows.find(item=>item.id===interviewId);
    const interview=row?.data||(interviewId==='local'||!interviewId?project:null);
    const plan=makeAudioRoughCutPlan({source,paper:aiPaper,interview,interviewId:interviewId||'local'});
    const urls=new Map();
    const getUrl=async id=>{
     if(urls.has(id))return urls.get(id);
     const mediaRow=workingRows.find(item=>item.id===id);
     const url=mediaRow?await cloud.url(mediaRow)
      :id==='local'||(!cloud.active&&id===interviewId)?videoUrl:null;
     if(!url)throw new Error('This interview is unavailable. Reopen it or reconnect the original video.');
     urls.set(id,url);return url;
    };
    const blob=await renderAudioRoughCut(plan,getUrl,{
     signal:controller.signal,onProgress:setAudioExportStatus
    });
    if(controller.signal.aborted)return;
    downloadBlob(roughCutFileName(source==='assembly'?collectionTitle+(
      readAssemblyCuts(aiPaper).items.length>1?' - '+activeCutName(aiPaper):'')
      :interview?.displayName||interview?.title||'interview',source),blob);
    setExportOpen(false);
   }catch(e){
    if(!controller.signal.aborted)setError(e.message||'Could not create the MP3 rough cut.');
   }finally{
    audioExportController.current=null;
    setAudioExportStatus(null);
   }
  };
  const downloadPaper=async(paper,kind='xml')=>{
    const first=paper.sources[paper.blocks[0].sourceId];
    if(!first)throw new Error('The first Assembly passage has no source interview.');
    paper={...paper,sourceColourOrder:sourceColourOrder(paper.sourceColourOrder,
      [...new Set([...colourSourceIds,...Object.keys(paper.sources)])]),
      sequenceRate:paper.sequenceRate || {fpsNum:first.fpsNum,fpsDen:first.fpsDen}};
    const output={...first,title:`${collectionTitle}${readAssemblyCuts(paper).items.length>1?' — '+activeCutName(paper):''} — Paper edit`,paperExport:paper};
    const timeline=kind==='resolve-edl'?buildResolveEdl(output):kind==='fcpxml'?buildFcpXml(output):buildXml(output);
    // The project root owns Assembly state even if another interview is open.
    if(cloud.active?.id===collectionId){
      commit({...project,paperEdit:paper});
    }else if(collectionRoot){
      await cloud.saveInterview(collectionRoot,{...collectionRoot.data,paperEdit:paper});
    }
    const isEdl=kind==='resolve-edl',suffix=assemblyCutExportSuffix(paper);
    const name=kind==='xml'? `takefold-assembly${suffix}.xml`:
      kind==='fcpxml'? `takefold-assembly${suffix}-final-cut.fcpxml`:
      isEdl? `takefold-assembly${suffix}-resolve.edl`:`takefold-assembly${suffix}-resolve.xml`;
    downloadFile(name,timeline,isEdl?'text/plain':'application/xml');
    setPaperSetup(null);setExportOpen(false);setError('');
  };
  const confirmPaperExport=async event=>{
    event.preventDefault();
    const form=new FormData(event.currentTarget),sources={...paperSetup.sources};
    for(const id of new Set(paperSetup.blocks.map(b=>b.sourceId))){
      const value=form.get(id);
      if(value){const [fpsNum,fpsDen]=String(value).split('/').map(Number);sources[id]={...sources[id],fpsNum,fpsDen,frameStatus:'confirmed-cfr'};}
    }
    try{await downloadPaper({...paperSetup,sources},paperExportKind);}catch(e){setError(e.message);}
  };

  // Advanced Export can correct any interview's original media metadata.
  // It must never modify transcript cuts or replace Assembly passages.
  const saveExportSourceMetadata=async(id,metadata)=>{
    if(!id)throw new Error('Choose an interview before saving source information.');
    if(id===cloud.active?.id || (id==='local'&&!cloud.active)){
      const updated={...project,...metadata};
      commit(updated);
      return updated;
    }
    const row=workingRows.find(item=>item.id===id);
    if(!row)throw new Error('This interview is not available. Reopen the project and try again.');
    await cloud.flush();
    const saved=await cloud.saveInterview(row,{...row.data,...metadata});
    return saved?.data || {...row.data,...metadata};
  };
  const openTranslation=(source='assembly',interviewId=null,selectedPassageId=null)=>{
    if(!session){setAuthOpen(true);return;}
    try{
      if(source==='assembly'){
        if(!aiPaper?.blocks?.length)throw new Error('Add Assembly passages before translating.');
        const sources={...aiPaper.sources};
        for(const id of Object.keys(sources)){
          const latest=workingRows.find(item=>item.id===id)?.data;
          if(latest?.words?.length)sources[id]={...sources[id],words:latest.words};
        }
        setTranslationRequest({project:{...project,paperExport:{...aiPaper,sources}},
          label:collectionTitle||'Takefold Assembly',
          selectedPassageId:aiPaper.blocks.some(block=>block.id===selectedPassageId)?selectedPassageId:null});
      }else{
        const row=workingRows.find(item=>item.id===interviewId);
        const interview=row?.data || (interviewId==='local'||!interviewId?project:null);
        if(!interview?.words?.length)throw new Error('Transcribe this interview before translating.');
        setTranslationRequest({project:interview,
          label:interview.displayName||interview.title||interview.filename||'Interview'});
      }
      setExportOpen(false);setError('');
    }catch(e){setError(e.message);}
  };
  const exportFile=async(kind,source='assembly',interviewId=null,savedSources={})=>{
    const timelineKind=['xml','resolve-xml','resolve-edl','fcpxml'].includes(kind);
    if(kind==='mp3'){await exportMp3(source,interviewId);return;}
    if(!timelineKind&&!['txt','srt'].includes(kind)){setError('Unsupported export format.');return;}
    if(source==='assembly'){
      try{
        if(!aiPaper?.blocks?.length)throw new Error('Add a passage to your Assembly before exporting.');
        if(!timelineKind){
          const output={...project,paperExport:aiPaper};
          downloadFile(`takefold-assembly${assemblyCutExportSuffix(aiPaper)}.${kind}`,kind==='txt'?buildText(output):buildSrt(output),'text/plain');
          setExportOpen(false);return;
        }
        const sources={...aiPaper.sources};
        for(const id of Object.keys(sources)){
          const latest=savedSources[id] || workingRows.find(row=>row.id===id)?.data;
          if(latest && ['confirmed-cfr','detected-cfr','demo-cfr','variable-fps'].includes(latest.frameStatus)){
            const {filename,fpsNum,fpsDen,frameStatus,width,height,audioChannels,audioSampleRate,timecode}=latest;
            sources[id]={...sources[id],filename:filename||sources[id].filename,fpsNum,fpsDen,frameStatus,width,height,audioChannels,audioSampleRate,timecode};
          }
        }
        const paper={...aiPaper,sources};
        if(paper.blocks.some(block=>sources[block.sourceId]?.frameStatus==='variable-fps'))
          throw new Error('An interview has variable frame timing. Convert it to constant frame rate and import that copy before XML export.');
        if(paper.blocks.some(block=>!['confirmed-cfr','detected-cfr','demo-cfr'].includes(sources[block.sourceId]?.frameStatus))){
          setError('');setPaperExportKind(kind);setExportOpen(false);setPaperSetup(paper);return;
        }
        await downloadPaper(paper,kind);
      }catch(e){setError(e.message);}
      return;
    }
    if(source!=='interview'){setError('Choose a valid export source.');return;}
    const interview=savedSources[interviewId] || workingRows.find(row=>row.id===interviewId)?.data ||
      (interviewId==='local' ? project : null);
    if(!interview){setError('The selected interview could not be found. Choose another interview.');return;}
    if(timelineKind && interview.frameStatus==='variable-fps'){
      setError('This interview has variable frame timing. Convert it to constant frame rate before making selects for XML export.');return;
    }
    if(timelineKind && !['confirmed-cfr','detected-cfr','demo-cfr'].includes(interview.frameStatus)){
      setError('Please verify the original frame rate in Export → Advanced source settings.');
      return;
    }
    const base=(interview.title||interview.filename||'interview').replace(/[^a-z0-9_-]+/gi,'-').toLowerCase();
    try{
      const contents=kind==='resolve-edl'?buildResolveEdl(interview):
        kind==='fcpxml'?buildFcpXml(interview):
        timelineKind?buildXml(interview):kind==='srt'?buildSrt(interview):buildText(interview);
      const extension=kind==='resolve-edl'?'edl':kind==='fcpxml'?'fcpxml':timelineKind?'xml':kind;
      const filename=kind.startsWith('resolve-')?`${base}-resolve.${extension}`:kind==='fcpxml'?`${base}-final-cut.fcpxml`:`${base}.${extension}`;
      downloadFile(filename,contents,timelineKind&&kind!=='resolve-edl'?'application/xml':'text/plain');
      setExportOpen(false);setError('');
    }catch(e){setError(e.message);}
  };

  const addFromLibrary=async(assetId,targetProjectId=null)=>{
    try{
      setError('');
      const linked=await cloud.reuseMedia(assetId,targetProjectId);
      setPaperSourceId(linked.id);
      setWorkspaceMode('paper');setFeedbackOpen(false);resetFilesOnProjectEntry();setScreen('editor');
      setMediaLibraryOpen(false);
    }catch(err){setError(err.message||'Could not add this video from Media Library.');}
  };
  const newProject=async()=> {
    if(locked)return false;
    try{await cloud.flush();}catch(e){setError(e.message);return false;}
    if(!cloud.active && !project.isDemo && project.words.length && !window.confirm('Start a new project? This local project has not been saved to cloud.'))return false;
    resetFilesOnProjectEntry();setScreen('editor');cloud.detach();setPaperSourceId('');resetWorkspace({...demoProject,projectName:'Untitled project',title:'Untitled project',duration:0,words:[],cuts:[],isDemo:false,frameStatus:'unverified'});
    return true;
  };
  const openCloudImportFromProjects=async()=>{
    if(!session){setAuthOpen(true);return;}
    if(await newProject())setCloudImportOpen(true);
  };

  const showProjects=async()=>{if(locked)return;try{await cloud.flush();pauseEditing();setScreen('projects');}catch(e){setError(e.message);}};
  const showAccount=async()=>{if(locked)return;try{await cloud.flush();pauseEditing();setScreen('account');}catch(e){setError(e.message);}};

  const handleSignOut=async ()=> {
    try {
      await cloud.flush();
      await signOut();
      setAuthOpen(false);
    } catch {
      setError('Could not sign out. Check your connection and try again.');
    }
  };

  const finishRecovery=()=> {
    setAuthOpen(false);
    window.history.replaceState({},document.title,window.location.pathname);
  };

  if(marketingView)return <GretaHome onLogin={()=>{
    window.history.pushState({},'', '/app');
    setMarketingView(false);
    setScreen('projects');
    if(!session)setAuthOpen(true);
  }}/>;
  return (
    <div className={`app-shell screen-${screen} mode-${workspaceMode}${sidebarCollapsed?' sidebar-collapsed':''}`}>
      {screen==='editor' && cloud.busy && !cloud.renaming && createPortal(
        <div className="modal-backdrop takefold-loading-backdrop" role="status" aria-live="polite">
          <div className="modal takefold-project-loading-modal">
            <div className="takefold-project-loading-brand">
              <img className="takefold-project-loading-logo takefold-project-loading-logo-dark" src="/brand/takefold-mono.png" alt="Takefold" />
              <img className="takefold-project-loading-logo takefold-project-loading-logo-light" src="/brand/takefold-logo.png" alt="" aria-hidden="true" />
            </div>
            <h2>{cloud.status==='Uploading video…'?'Uploading video…':'Loading project…'}</h2>
            <p>Please keep this tab open until this finishes.</p>
          </div>
        </div>, document.body
      )}
      <header className="topbar">
        <div className="brand">
          <img className="takefold-logo takefold-logo-dark" src="/brand/takefold-mono.png" alt="Takefold" />
          <img className="takefold-logo takefold-logo-light" src="/brand/takefold-logo.png" alt="" aria-hidden="true" />
          <span className="brand-beta">BETA</span>
        </div>
        {screen==='editor' && <button className="back-projects" disabled={locked} onClick={showProjects} title="Back to projects"><SafeIcon icon={FiIcons.FiArrowLeft}/> Projects</button>}
        {screen==='editor' && <div className="project-title">
          
          <ProjectTitle key={collectionId || 'local'} title={collectionTitle} disabled={locked} onSave={async name=>{if(collectionId)return cloud.rename(collectionId,name);commit({...project,projectName:name});return true;}}/>
          <SafeIcon icon={FiIcons.FiChevronDown} />
        </div>}
        <div className="top-actions"><a className="takefold-help-link" href="/help" target="_blank" rel="noopener noreferrer" title="Open Takefold Help Centre in a new tab" aria-label="Help Centre (opens in new tab)"><SafeIcon icon={FiIcons.FiBookOpen}/><span>Help</span></a><details className="account-menu" ref={accountMenuRef} onClick={event=>{if(event.target.closest?.("button"))event.currentTarget.open=false;}}><summary aria-label="Account and settings" title="Account and settings"><SafeIcon icon={FiIcons.FiSettings}/></summary><div className="account-menu-panel">
          {session && <small className="account-menu-email" title={session.user.email}>{session.user.email}</small>}
          <button type="button" className="account-menu-row" onClick={showAccount} disabled={locked}><SafeIcon icon={FiIcons.FiUser}/><span>Account &amp; billing</span></button>
          <button type="button" className="account-menu-row" onClick={toggleTheme}>
            <SafeIcon icon={theme==='dark'?FiIcons.FiSun:FiIcons.FiMoon}/>
            <span>{theme==='dark'?'Switch to Light Theme':'Switch to Dark Theme'}</span>
          </button>
          {screen==='editor'&&<details className="account-menu-shortcuts">
            <summary className="account-menu-row"><SafeIcon icon={FiIcons.FiCommand}/><span>Keyboard shortcuts</span></summary>
            <p>{workspaceMode==='transcript'
              ? 'Space — play/pause · Ctrl/⌘ F — search · Ctrl/⌘ Z — undo'
              : 'E — add selected words · Space — play Assembly · Enter — audition passage · Ctrl/⌘ F — search · Ctrl/⌘ Z — undo'}</p>
          </details>}
          <div className="account-menu-separator" role="separator"/>
          <button type="button" className="account-menu-row" onClick={session ? handleSignOut : ()=>setAuthOpen(true)} disabled={authLoading || locked}><SafeIcon icon={session?FiIcons.FiLogOut:FiIcons.FiLogIn}/><span>{session?'Sign out':'Sign in'}</span></button>
        </div></details>
          {screen==='editor' && <>
          <span className="save-state" role="status"><SafeIcon icon={/failed|Unsaved/.test(cloud.status)?FiIcons.FiAlertCircle:/Saving|Uploading/.test(cloud.status)?FiIcons.FiClock:FiIcons.FiCheckCircle} /> {cloud.status || saveStatus}</span>
          <span className="action-divider" />
          <button className="history-button" title="Undo (Ctrl/⌘ Z)" disabled={!history.length} onClick={undo}>
            <SafeIcon icon={FiIcons.FiCornerUpLeft} /> Undo
          </button>
          <button className="history-button" title="Redo (Ctrl/⌘ Shift Z)" disabled={!future.length} onClick={redo}>
            <SafeIcon icon={FiIcons.FiCornerUpRight} /> Redo
          </button>
          <button className="share-project" disabled={locked||!collectionId} onClick={async()=>{if(workspaceMode!=='paper' && await openPaper()===false)return;setReviewOpen(true);}}><SafeIcon icon={FiIcons.FiShare2}/> Share</button>
          <button className="export-button" disabled={locked} title="Export the Paper Edit Assembly or a single interview transcript edit" onClick={()=>{setError('');setExportOpen(true);}}>
            <SafeIcon icon={FiIcons.FiUpload} /> Export <SafeIcon icon={FiIcons.FiChevronDown} />
          </button>
          </>}
        </div>
      </header>

      {screen==='account' ? <AccountPage session={session} onBack={showProjects}
        onSignIn={()=>setAuthOpen(true)} requestPasswordReset={requestPasswordReset}/>
      : screen==='projects' ? <ProjectsHome rows={cloud.rows} trashRows={cloud.trashRows} onImportCloud={openCloudImportFromProjects} getUrl={cloud.url} mediaLibrary={cloud.mediaLibrary} onReuse={addFromLibrary} busy={locked} loading={authLoading} signedIn={!!session} onOpen={openProject} onNew={newProject} onRename={cloud.rename} onDelete={requestDeleteProject} onPermanentDelete={requestPermanentDelete} onRestore={cloud.restoreProject} onMerge={requestMerge} onSignIn={()=>setAuthOpen(true)} hasDraft={!!(cloud.active || videoUrl || project.words.length)} onResume={()=>{resetFilesOnProjectEntry();setScreen('editor');}} error={error || authError}/> : <div className="workspace">
        <aside className={`sidebar${batchReport?.active?" is-batch-processing":""}`} id="project-sidebar" aria-hidden={sidebarCollapsed}>
          <div className="sidebar-section"><span>FILES · {collectionMedia.length}</span>
            <button type="button" className="files-collapse-button" aria-label="Hide Files panel" title="Hide Files panel" aria-controls="project-sidebar" aria-expanded="true" onClick={()=>setSidebarCollapsed(true)}>
              <SafeIcon icon={FiIcons.FiChevronLeft}/>
            </button>
          </div>
          <div className="sidebar-media-actions">
            <MediaAddMenu disabled={locked} signedIn={Boolean(session)}
              hasProject={Boolean(collectionId)}
              onComputer={()=>fileRef.current?.click()}
              onCloud={()=>setCloudImportOpen(true)}
              onLibrary={()=>setMediaLibraryOpen(true)}/>
          </div>
          {batchReport&&<BatchImportProgress report={batchReport} stopping={batchStopping}
            onStop={()=>{batchStop.current=true;setBatchStopping(true);}}
            onDismiss={()=>setBatchReport(null)}/>}
          <nav aria-label="Project files" className="file-tiles">{collectionMedia.map(row=><div className="cloud-project-row" key={row.id}>
            <button className="media-card interview-source" style={interviewColour(row.id,colourSourceIds)} disabled={locked || !row.media_ready || renamingInterviewId===row.id} onClick={()=>selectInterview(row.id)} aria-current={(workspaceMode==='paper'?selectedInterviewId:cloud.active?.id)===row.id?'true':undefined}>
              <MediaThumbnail row={row} getUrl={cloud.url}/>
              <div className="file-tile-details">
                <strong className="file-tile-filename" title={originalInterviewFilename(row.data)}>{originalInterviewFilename(row.data)}</strong>
                {interviewAlias(row.data)&&<span className="file-tile-alias" title={`Display name: ${interviewAlias(row.data)}`}>(<b>{interviewAlias(row.data)}</b>)</span>}
                <small className={`file-tile-status${row.data.transcriptionError?' is-failed':''}`}>{interviewStatus(row,{processingId,processingText:batchReport?.active?'Transcribing…':uploadState,activeId:cloud.active?.id,localProject:project})}</small>
              </div>
            </button>
            {renamingInterviewId===row.id?<form className="file-rename-form" onSubmit={event=>{event.preventDefault();saveInterviewName(row);}}>
              <label htmlFor={`rename-interview-${row.id}`}>Interview display name</label>
              <input id={`rename-interview-${row.id}`} autoFocus maxLength={100} required value={interviewNameDraft} onChange={event=>setInterviewNameDraft(event.target.value)} onKeyDown={event=>{if(event.key==='Escape'){event.stopPropagation();setRenamingInterviewId(null);}}}/>
              <small>Original video filename stays unchanged for Premiere.</small>
              <div><button type="submit" disabled={!interviewNameDraft.trim()||savingInterviewName}>Save</button><button type="button" disabled={savingInterviewName} onClick={()=>setRenamingInterviewId(null)}>Cancel</button></div>
            </form>:<FileActionsMenu row={row} name={interviewDisplayName(row.data)}
              disabled={locked}
              canTranscribe={Boolean(session&&row.media_ready&&row.data.duration&&row.data.duration<=MAX_DURATION_SECONDS)}
              transcribing={processingId===row.id}
              onTranscribe={transcribe}
              onRename={startInterviewRename}
              onDelete={deleteInterview}/>}
          </div>)}</nav>
          <input
            ref={fileRef}
            className="hidden-input"
            type="file"
            accept="video/mp4,video/webm,.mp4,.webm"
            multiple
            onChange={event=> {
              const files=Array.from(event.target.files||[]);
              event.target.value='';
              if(!files.length)return;
              if(session)void importBatch(files);
              else if(files.length===1)void loadFile(files[0]);
              else{setAuthOpen(true);setError('Sign in to batch-upload interviews and transcribe them automatically.');}
            }}
          />
          {!cloud.active && <button className="media-card" onClick={()=> fileRef.current?.click()}>
            <div className="media-thumbnail"><SafeIcon icon={FiIcons.FiFilm} /></div>
            <div>
              <strong>{videoUrl ? project.filename : 'Add source video'}</strong>
              <small>{videoUrl ? cloud.active ? 'Private cloud video' : 'Not saved to cloud yet' : 'Choose an MP4 video'}</small>
            </div>
          </button>}
          {session && !cloud.active && videoUrl && project.duration>0 && <button className="primary-wide" disabled={cloud.busy || transcribing} onClick={()=>cloud.upload(sourceFileRef.current,p=>setUploadState('Saving video · '+p+'%'))}>Retry saving video to cloud</button>}

          {cloud.status==='Cloud save failed' && cloud.active && <button onClick={cloud.retry}>Retry cloud save</button>}
          {uploadState && !batchReport && <div className="media-status"><span className="green-dot" />{uploadState}</div>}



          {workspaceMode==='transcript' && !project.isDemo && project.alignment!=='elevenlabs' && project.words.length>0 && <button className="settings-link" disabled={!videoUrl || transcribing || cloud.busy || refiningCuts || !project.cuts.length} onClick={()=>refineTiming(false)} title="Refine existing transcript cuts using nearby quieter audio. Original word timestamps stay unchanged. Undo reverses this change.">
            <SafeIcon icon={FiIcons.FiScissors} /> Refine cut timing · beta
          </button>}

          <div className="sidebar-bottom">
            <div className="demo-label">
              <SafeIcon icon={FiIcons.FiAlertCircle} />
              <div>
                <strong>{project.isDemo ? 'DEMO PROJECT' : cloud.active ? 'CLOUD PROJECT' : 'LOCAL PROJECT'}</strong>
                <p>{project.isDemo ? 'Sample transcript & cuts. No video or processing attached.' : cloud.active ? 'Video and edits save privately to your account.' : session ? 'Video saves automatically after import. Wait for Saved to cloud before closing.' : 'Sign in to save your video and edits to cloud.'}</p>
              </div>
            </div>
            <button type="button" className="sidebar-theme-toggle" onClick={toggleTheme}
              aria-label={theme==='dark'?'Switch to light theme':'Switch to dark theme'}
              title={theme==='dark'?'Switch to light theme':'Switch to dark theme'}>
              <SafeIcon icon={theme==='dark'?FiIcons.FiSun:FiIcons.FiMoon}/>
              <span>{theme==='dark'?'Light theme':'Dark theme'}</span>
            </button>
          </div>
        </aside>
        {sidebarCollapsed&&<button type="button" className="sidebar-theme-collapsed" onClick={toggleTheme}
          aria-label={theme==='dark'?'Switch to light theme':'Switch to dark theme'}
          title={theme==='dark'?'Switch to light theme':'Switch to dark theme'}>
          <SafeIcon icon={theme==='dark'?FiIcons.FiSun:FiIcons.FiMoon}/>
        </button>}
        <div className="sidebar-edge">
          <button type="button" className="sidebar-toggle" aria-expanded={!sidebarCollapsed}
            aria-controls="project-sidebar" aria-label={sidebarCollapsed?'Show Files panel':'Hide Files panel'}
            title={sidebarCollapsed?'Show Files panel':'Hide Files panel'}
            onClick={()=>setSidebarCollapsed(value=>!value)}>
            <SafeIcon icon={sidebarCollapsed?FiIcons.FiChevronRight:FiIcons.FiChevronLeft}/>
          </button>
        </div>


        <main className="main-content">
          {videoUrl && !cloud.active && !cloud.busy && <div className="error-banner" role="status">{session ? 'This video is not saved to cloud yet. Keep this tab open until Saved to cloud appears.' : 'Local preview only. Sign in to save this video before refreshing.'}</div>}

          {transcribing && !batchReport?.active && <div className="transcribing-banner" role="status" aria-live="polite">
            <SafeIcon icon={FiIcons.FiLoader} className="spinner" />
            <div><strong>Transcribing — please wait</strong><span>{uploadState} · Keep this tab open.</span></div>
          </div>}
          {(error || authError) && (
            <div className="error-banner">
              <SafeIcon icon={FiIcons.FiAlertTriangle} />{error || authError}
              <button onClick={()=> {setError('');clearAuthError();}}><SafeIcon icon={FiIcons.FiX} /></button>
            </div>
          )}
          {workspaceMode==='transcript' && videoUrl && project.duration>0 && !project.words.length && !transcribing && <div className="next-step-banner"><div><strong>Video ready — next, create your transcript</strong><p>{session ? 'Transcribe once, then select words to cut or restore footage.' : 'Sign in to transcribe this video.'}</p></div><button className="export-button" disabled={cloud.busy} onClick={session ? transcribe : ()=>setAuthOpen(true)}>{session ? 'Transcribe video' : 'Sign in to transcribe'}</button></div>}
          <div className="workspace-modes">
            {sidebarCollapsed&&<button type="button" className="files-reopen-button" aria-expanded="false" aria-controls="project-sidebar" title="Show Files panel" onClick={()=>setSidebarCollapsed(false)}>
              <SafeIcon icon={FiIcons.FiSidebar}/><span>Files</span>
            </button>}
            <div className="mode-switch" role="group" aria-label="Editing mode">
              <button type="button" disabled={locked} aria-pressed={isEditorTabActive('paper',workspaceMode,feedbackOpen)} title="Build a story from multiple interview transcripts" onClick={openPaper}>Paper Edit</button>
              <button type="button" disabled={locked} aria-pressed={isEditorTabActive('transcript',workspaceMode,feedbackOpen)} title="Cut or restore footage in an individual interview" onClick={()=>{setFeedbackOpen(false);void openTranscript();}}>Transcript</button>
              <button type="button" disabled={locked||!collectionId} aria-pressed={isEditorTabActive('feedback',workspaceMode,feedbackOpen)} title="Review client comments and suggestions alongside your Assembly" onClick={async()=>{const ok=await openPaper();if(ok!==false)setFeedbackOpen(true);}}>Feedback</button>
            </div>

          </div>
          {importing && !batchReport?.active && <p className="import-progress" role="status">{uploadState}</p>}
          {workspaceMode==='paper' ? <PaperEdit key={collectionId || 'local'} project={project} rows={workingRows} activeId={cloud.active?.id} sourceId={selectedInterviewId || ''} onSourceChange={setPaperSourceId} onChange={commit} getUrl={cloud.url} onError={setError} getAccessToken={getMeaningToken}
            projectTitle={collectionTitle} playbackControlRef={paperPlaybackRef} storyProjectId={collectionId} onSelectInterview={selectInterview}
            feedbackOpen={feedbackOpen} reviewRefreshKey={reviewRefreshKey} onFeedbackOpenChange={setFeedbackOpen} onManageReview={()=>{setReviewAutoCreate(false);setReviewOpen(true);}} onStartReview={()=>{setReviewAutoCreate(true);setReviewOpen(true);}} onAcceptFeedback={async id=>{const next=await cloud.acceptReview(id);if(next)commit(next);else throw new Error('This suggestion could not be accepted.');}}
            findSelectsOpen={findSelectsOpen} onFindSelectsChange={setFindSelectsOpen} findButtonRef={findSelectsButtonRef} onImport={()=>fileRef.current?.click()} onTranscribe={transcribe} busy={locked} processingId={processingId} processingText={batchReport?.active?'Transcribing…':uploadState} signedIn={!!session} onSignIn={()=>setAuthOpen(true)} localUrl={videoUrl}
            onRefine={openRefine}
            onBeforeCutSwitch={async()=>{if(cloud.active)await cloud.flush();}}
            onRecoverStory={earlier=>commit({...project,paperEdit:appendEarlierStory(project.paperEdit,earlier)})} onRenameSpeaker={renameInterviewSpeaker} onCleanupSource={applyInterviewCleanup}/>
           : workspaceMode==='refine' ? <RefineReview
             key={collectionId||'local'} project={project} rows={workingRows}
             activeId={cloud.active?.id} getUrl={cloud.url} localUrl={videoUrl}
             busy={locked} onChange={commit} onError={setError}
             onBack={openPaper} onExport={()=>setExportOpen(true)}
             playbackRef={paperPlaybackRef}/>
           : <><div className="editor-grid" ref={transcriptColumns} style={{'--transcript-left':`${transcriptSplit}fr`,'--transcript-right':`${100-transcriptSplit}fr`}}>
            <Transcript
              project={project}
              onRenameSpeaker={(speaker,name)=>renameInterviewSpeaker(cloud.active?.id,speaker,name)}
              sourceId={cloud.active?.id} reviewProjectId={collectionId}
              interviews={collectionMedia} selectedInterviewId={toolbarSourceId || ''} onSelectInterview={selectInterview} interviewPickerDisabled={locked}
              meaningMatches={transcriptMeaningMatches}
              findSelectsButton={<FindSelectsButton open={findSelectsOpen}
                onToggle={()=>{stopFoundTake();setFindSelectsOpen(open=>!open);setFindSelectsNotice('');}}
                available={findSelectsSources.length>0}
                disabled={locked} buttonRef={findSelectsButtonRef}/>}
              findSelectsPanel={findSelectsOpen && findSelectsSources.length>0 ? <section className="transcript-find-selects-panel" id="editor-find-selects-panel" aria-label="Find selects">
                <header className="paper-explore-heading"><span className="selects-scope">Find Selects <strong>· {findSelectsSources.length} project interviews</strong></span>
                  <button type="button" aria-label="Close Find selects" onClick={()=>{stopFoundTake();setFindSelectsOpen(false);findSelectsButtonRef.current?.focus();}}>×</button>
                </header>
                <div className="paper-explore-body">
                  <MeaningSearch key={aiRootId||aiSource?.id||'local'} source={aiSource} sources={findSelectsSources}
                    getAccessToken={getMeaningToken} onMatches={setTranscriptMeaningMatches}
                    isAdded={match=>Boolean(aiPaper.blocks?.some(b=>b.sourceId===match.sourceId&&
                      b.start<=match.words[0].start&&b.end>=match.words.at(-1).end))}
                    onPreview={previewFoundTake} onStop={stopFoundTake}
                    onAdd={addAiWordsToStory}/>
                  {findSelectsNotice && <p className="media-status" role="status">{findSelectsNotice}</p>}
                </div>
              </section> : null}
              selected={selected}
              priorityWordIds={aiSource?sourceWordPriority(aiPaper,aiSource):{}}
              cleanupControl={aiSource&&<AiCleanup key={aiSource.id}
                source={{...aiSource,cuts:project.cuts||[],clips:project.clips}}
                sourceId={aiSource.id}
                priorityWordIds={sourceWordPriority(aiPaper,aiSource)}
                assemblyWordIds={storyWordIds(project.words,aiPaper.blocks||[],aiSource.id)}
                getAccessToken={getMeaningToken} onApply={applyInterviewCleanup}
                onPreview={previewAiWords} disabled={locked||!session}/>}
              onCycleSourcePriority={cycleTranscriptSourcePriority}
              priorityBusy={locked||sourcePriorityBusy}
              onSelect={selectWord}
              onTogglePlayback={play}
              onSelectRange={ids=>{pauseEditing();setSelected(ids);setAnchor(project.words.findIndex(word=>word.id===ids[0]));}}
              onOmit={omit}
              currentTime={currentTime}
              showRemoved={showRemoved}
              onShowRemovedChange={setShowRemoved}
              search={search}
              onSearch={setSearch}
              onRemove={remove}
              onRestore={restore}
              onCorrect={correct}
              onTranscribe={session ? transcribe : ()=>setAuthOpen(true)}
              transcribeDisabled={!videoUrl || !project.duration || project.duration>MAX_DURATION_SECONDS || cloud.busy || transcribing}
              transcribing={transcribing}
              signedIn={Boolean(session)}
            />
            <div className="transcript-divider paper-divider" role="separator" tabIndex={0}
              aria-label="Resize transcript and preview" aria-orientation="vertical"
              aria-controls="transcript-editor-panel transcript-preview-panel"
              aria-valuemin={35} aria-valuemax={65} aria-valuenow={Math.round(transcriptSplit)}
              aria-valuetext={`${Math.round(transcriptSplit)} percent transcript width`}
              title="Drag to resize · Double-click to reset"
              onPointerDown={e=>{if(e.button!==0)return;e.preventDefault();resizingTranscript.current=true;e.currentTarget.setPointerCapture(e.pointerId);}}
              onPointerMove={e=>{if(resizingTranscript.current)resizeTranscriptFromPointer(e);}}
              onPointerUp={e=>{resizingTranscript.current=false;if(e.currentTarget.hasPointerCapture(e.pointerId))e.currentTarget.releasePointerCapture(e.pointerId);}}
              onPointerCancel={()=>{resizingTranscript.current=false;}}
              onLostPointerCapture={()=>{resizingTranscript.current=false;}}
              onDoubleClick={()=>changeTranscriptSplit(55)}
              onKeyDown={e=>{if(['ArrowLeft','ArrowRight','Home','End'].includes(e.key)){e.preventDefault();changeTranscriptSplit(e.key==='Home'?35:e.key==='End'?65:transcriptSplit+(e.key==='ArrowLeft'?-2:2));}}}>
              <span />
            </div>
            <Preview
              activeClipId={activeClipRef.current}
              videoRef={videoRef}
              videoUrl={videoUrl}
              project={project}
              playing={playing}
              onPlay={play}
              currentTime={currentTime}
              previewMode={previewMode}
              onModeChange={mode=> { pauseEditing(); setPreviewMode(mode); }}
              onTimeUpdate={onTimeUpdate}
              onEnded={onEnded}
              onMetadata={onMetadata}
            />
          </div>

          <Timeline videoUrl={videoUrl} project={project} currentTime={currentTime} activeClipId={activeClipRef.current} onSeek={seek} onChange={next=> { commit(next); setSelected([]); }} onInteractionStart={pauseEditing} /></>}

          <div className="bottom-strip">
            <span><span className="green-dot" /> {cloud.active ? 'CLOUD PROJECT' : 'LOCAL PROJECT'}</span>
            <span>{workspaceMode!=='transcript' ? `${project.paperEdit?.blocks.length || 0} STORY PASSAGES` : `${project.cuts.length} CUTS · ${Math.round(sequenceDuration(project)*10)/10}s RETAINED`}</span>
            <span>{workspaceMode==='refine' ? 'SELECTS → CLEAN CUTS → EXPORT' : workspaceMode==='paper' ? 'YOUR INTERVIEWS → ONE PROJECT STORY' : 'INTERVIEW CLEANUP'}</span>
          </div>
        </main>
      </div>}

      {deleteProjectRow&&<ProjectDeleteDialog project={deleteProjectRow} mode={deleteProjectMode}
        onPreview={cloud.previewProjectDelete} onConfirm={confirmDeleteProject}
        onClose={()=>setDeleteProjectRow(null)}/>}
      {mergeProjectRow&&<ProjectMergeDialog source={mergeProjectRow}
        projects={cloud.rows.filter(row=>!row.data?.collectionId)}
        onPreview={cloud.previewMerge} onMerge={confirmMerge}
        onClose={()=>setMergeProjectRow(null)}/>} 
      {refiningCuts && <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Refining cut timing"><div className="modal"><h2>Refining cut timing…</h2><p>Checking the audio at cut edges. Please wait.</p></div></div>}
      {authOpen && (
        <AuthDialog
          onClose={()=> setAuthOpen(false)}
          onSignIn={signIn} onSignUp={signUp}
          authError={authError}
          clearAuthError={clearAuthError}
          requestPasswordReset={requestPasswordReset}
          updatePassword={updatePassword}
          recoveryMode={Boolean(recoveryRoute || recoverySession)}
          onRecoveryComplete={finishRecovery}
        />
      )}

      {cloudImportOpen&&<CloudImportDialog onClose={()=>setCloudImportOpen(false)}
        getAccessToken={getMeaningToken} onImport={files=>importBatch(files)}/>}
      {mediaLibraryOpen&&collectionId&&<MediaLibraryPicker assets={cloud.mediaLibrary} projectInterviews={collectionMedia}
        busy={locked} getUrl={cloud.url} onClose={()=>setMediaLibraryOpen(false)}
        onUse={assetId=>addFromLibrary(assetId,collectionId)}/>}
      {reviewOpen && collectionId && <ReviewManager projectId={collectionId} flush={cloud.flush} autoCreate={reviewAutoCreate} onClose={()=>{setReviewOpen(false);setReviewAutoCreate(false);setReviewRefreshKey(v=>v+1);}}/> }
      {translationRequest&&<TranslationDialog key={translationRequest.label}
        project={translationRequest.project} sourceLabel={translationRequest.label}
        selectedPassageId={translationRequest.selectedPassageId||null}
        getAccessToken={getMeaningToken} onClose={()=>setTranslationRequest(null)}/>}
      {exportOpen && <ExportDialog project={project} paper={aiPaper} interviews={collectionMedia.length
        ? collectionMedia.map(row=>workingRows.find(item=>item.id===row.id)||row)
        : [{id:cloud.active?.id||'local',data:project}]}
        defaultInterviewId={workspaceMode==='transcript'?cloud.active?.id:selectedInterviewId}
        title={collectionTitle} onExport={exportFile} onTranslate={openTranslation} onSaveSourceMetadata={saveExportSourceMetadata}
        audioExportStatus={audioExportStatus} onCancelAudio={cancelAudioExport}
        onClose={()=>{if(audioExportRunning){cancelAudioExport();return;}setExportOpen(false);setError('');}} error={error}/>}

      {paperSetup && <div className="modal-backdrop"><div className="modal settings-modal" role="dialog" aria-modal="true" aria-labelledby="paper-export-title">
        <button className="modal-close" aria-label="Close export setup" onClick={()=>setPaperSetup(null)}>×</button>
        <h2 id="paper-export-title">Prepare your timeline export</h2>
        {error && <p className="error-banner" role="alert">{error}</p>}
        <p>Confirm the original source frame rates below using clip Properties in Premiere or Resolve. We’ll remember these settings for this Assembly.</p>
        <form onSubmit={confirmPaperExport}>
          <div className="form-grid">{[...new Set(paperSetup.blocks.map(b=>b.sourceId))].filter(id=>!['confirmed-cfr','detected-cfr','demo-cfr'].includes(paperSetup.sources[id]?.frameStatus)).map(id=><label className="full-field" key={id}>{paperSetup.sources[id]?.filename || paperSetup.sources[id]?.title || 'Missing interview'}<select name={id} required defaultValue=""><option value="" disabled>Choose video frame rate…</option>{[[24000,1001,'23.976'],[24,1,'24'],[25,1,'25'],[30000,1001,'29.97'],[30,1,'30'],[50,1,'50'],[60000,1001,'59.94'],[60,1,'60']].map(([n,d,label])=><option key={label} value={`${n}/${d}`}>{label} fps</option>)}</select></label>)}</div>
          <label className="export-confirm"><input type="checkbox" required/> These videos use a constant frame rate.</label>
          <p className="paper-hint">Variable-frame-rate recordings need conversion before accurate timeline export. {paperExportKind==='resolve-edl'?'Resolve EDL requires matching source and sequence frame rates; use Resolve XML for mixed rates.':'XML supports mixed frame rates using the Assembly’s sequence frame rate.'}</p>
          <button className="primary-wide" type="submit">Save settings &amp; export {paperExportKind==='resolve-edl'?'EDL':'XML'}</button>
        </form>
      </div></div>}
    </div>
  );
}

export default App;


async function getMeaningToken() {
  const {data,error}=await supabase.auth.getSession();
  if(error)throw error;
  return data.session?.access_token;
}
